"use client"

import * as React from "react"

import { ActionCard, type ActionItem } from "@/components/action-card"
import type { Invoice } from "@/features/invoices/components/InvoiceTable"
import { startOfCurrentWeek } from "@/features/invoices/filters"
import { useInvoices } from "@/features/invoices/hooks"

const MS_PER_DAY = 24 * 60 * 60 * 1000

function formatRelativePaid(updatedAt: Date, now: Date): string {
  const diffDays = Math.floor((now.getTime() - updatedAt.getTime()) / MS_PER_DAY)
  if (diffDays <= 0) return "Paid today"
  if (diffDays === 1) return "Paid 1d ago"
  if (diffDays < 7) return "Paid " + diffDays + "d ago"
  return "Paid " + updatedAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatOverdueDetail(invoice: Invoice, now: Date): string {
  const todayMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  )
  const expiration = new Date(
    invoice.expirationDate.getFullYear(),
    invoice.expirationDate.getMonth(),
    invoice.expirationDate.getDate(),
  )
  const days = Math.floor(
    (todayMidnight.getTime() - expiration.getTime()) / MS_PER_DAY,
  )
  if (days <= 0) return "Due today"
  if (days === 1) return "1 day overdue"
  return days + " days overdue"
}

function customerLabel(invoice: Invoice): string {
  return invoice.customer.name || invoice.customer.email
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return "Failed to load"
}

export function HomeActionCards() {
  const now = React.useMemo(() => new Date(), [])
  const weekStartIso = React.useMemo(
    () => startOfCurrentWeek(now).toISOString(),
    [now],
  )

  const overdueQuery = useInvoices({
    status: "overdue",
    createdBy: "me",
    page: 1,
    pageSize: 3,
  })

  const viewedQuery = useInvoices({
    status: "viewed",
    createdBy: "me",
    page: 1,
    pageSize: 3,
  })

  const paidQuery = useInvoices({
    status: "paid",
    createdBy: "me",
    updatedAtFrom: weekStartIso,
    page: 1,
    pageSize: 3,
  })

  const overdueItems: ActionItem[] =
    overdueQuery.data?.items.map((inv) => ({
      id: inv.id,
      customer: customerLabel(inv),
      amount: inv.netAmount,
      currency: inv.currency,
      detail: formatOverdueDetail(inv, now),
    })) ?? []

  const viewedItems: ActionItem[] =
    viewedQuery.data?.items.map((inv) => ({
      id: inv.id,
      customer: customerLabel(inv),
      amount: inv.netAmount,
      currency: inv.currency,
      detail: inv.course,
    })) ?? []

  const paidItems: ActionItem[] =
    paidQuery.data?.items.map((inv) => ({
      id: inv.id,
      customer: customerLabel(inv),
      amount: inv.netAmount,
      currency: inv.currency,
      detail: formatRelativePaid(inv.updatedAt, now),
    })) ?? []

  const paidHref =
    "/invoices?status=paid&created_by=me&updated_at_from=" +
    encodeURIComponent(weekStartIso)

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      <ActionCard
        title="Overdue"
        count={overdueQuery.data?.count ?? 0}
        items={overdueItems}
        viewAllHref="/invoices?status=overdue&created_by=me"
        variant="overdue"
        emptyMessage="No overdue invoices"
        loading={overdueQuery.isPending}
        error={
          overdueQuery.error ? errorMessage(overdueQuery.error) : undefined
        }
      />
      <ActionCard
        title="Viewed, not paid"
        count={viewedQuery.data?.count ?? 0}
        items={viewedItems}
        viewAllHref="/invoices?status=viewed&created_by=me"
        variant="viewed"
        emptyMessage="No viewed invoices awaiting payment"
        loading={viewedQuery.isPending}
        error={viewedQuery.error ? errorMessage(viewedQuery.error) : undefined}
      />
      <ActionCard
        title="Paid this week"
        count={paidQuery.data?.count ?? 0}
        items={paidItems}
        viewAllHref={paidHref}
        variant="paid"
        emptyMessage="No payments this week"
        loading={paidQuery.isPending}
        error={paidQuery.error ? errorMessage(paidQuery.error) : undefined}
      />
    </div>
  )
}
